import { Search } from 'lucide-solid';
import { createEffect, createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { RoomCanvas } from '../components/RoomCanvas';
import { ScreenHeader, BusySpinner, CardSection, ConfirmModal, ControlGate, InlineNotice } from '../components/ui';
import { OutputViewIndicator } from '../components/OutputViewIndicator';
import { Switch } from '../components/Switch';
import {
  effectPackageFromLiveEffect,
  effectPackageFromMirror,
  runningCardId,
  effectPackageUsesMusic,
  filterEffectPackages,
  operatorUnavailableReason,
  roomEffectLibrary,
  sameEffectLibrary,
  type EffectCatalogFilter,
  type EffectPackageItem,
} from '../lib/effectPackages';
import type { EffectLayout } from '../lib/effectLayout';
import { ROOM_UPDATED_NOTICE } from '../lib/operatorCopy';
import { can } from '../lib/permissions.generated';
import { reportError } from '../lib/plainErrors';
import type { FixtureValues } from '../lib/types';
import type { EffectPackageListResult } from '../wire-corpus/generated';
import { useVenue } from '../state/VenueProvider';
import { outputViewWireLabel, type OutputViewHolderView } from '../state/useOutputView';
import { EffectPackagePanel } from './EffectPackagePanel';
import { createTreeCardStartStates, DEFAULT_CARD_ID, TREE_CARD_START_ACTION_KEYS, treeCardPanel } from './treeCards';
import './EffectsScreen.css';
import { TEST_IDS, testIdFor } from '../testIds';

/**
 * B77's busy line after the name: the client and since when, in the words that were already here. The room
 * link's answer names no client, so its line carries only the time.
 */
function holderDetail(holder: OutputViewHolderView): string {
  const parts = [
    ...(holder.client ? [holder.client] : []),
    ...(holder.sinceMs !== undefined
      ? [`since ${new Date(holder.sinceMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`] : []),
  ];
  return parts.length ? ` (${parts.join(', ')})` : '';
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

export function EffectsScreen(): JSX.Element {
  const venue = useVenue();
  const [selectedPackageId, setSelectedPackageId] = createSignal(DEFAULT_CARD_ID);
  /**
   * A start asked for by double-clicking a card, named by package and CONSUMED by whoever performs it.
   *
   * Consumption is the point: the panel that starts it clears the request in the same turn, so a card
   * re-opened later can never re-run a start the operator asked for minutes ago.
   */
  const [pendingStart, setPendingStart] = createSignal('');
  function requestStart(packageId: string): void {
    if (cardStartStates.get(packageId)?.readiness().enabled === false) return;
    setPendingStart(packageId);
  }
  const [effectFilter, setEffectFilter] = createSignal<EffectCatalogFilter>('all');
  const [effectQuery, setEffectQuery] = createSignal('');
  const [rigReloadNotice, setRigReloadNotice] = createSignal('');
  /** The catalog a console with no cloud document reads for itself, in one bounded read per open. */
  const [queriedCatalog, setQueriedCatalog] = createSignal<unknown>([]);
  /**
   * The room's effect geometry comes from the provider, shared with every other panel on this screen.
   * It is deliberately NOT screen state: a copy that only lives while Effects is mounted is a copy that
   * disappears on every rig read, and each panel then decides on its own that the room has nothing.
   */
  const layout = venue.effectLayout;
  const layoutLoading = createMemo(() => venue.effectLayoutStatus() === 'loading');

  let catalogReadKey = '';
  let observedRigReloadRevision = venue.rigReloadRevision();

  const liveRoomFixtures = createMemo(() => new Map(
    (venue.controllerState()?.fixtures ?? []).map((fixture) => [fixture.deviceId, fixture]),
  ));
  const outputView = venue.outputView;
  const [outputTakeoverOpen, setOutputTakeoverOpen] = createSignal(false);
  const queriedEffectCatalog = createMemo(() => roomEffectLibrary(
    queriedCatalog(),
    venue.deviceDoc()?.commandCatalog,
  ));

  /**
   * The one effect catalog. The cloud console reads the pushed copy beside the command catalog; a direct
   * console, which has no cloud document, makes one bounded read when the screen opens.
   */
  const effectCatalog = createMemo<EffectPackageItem[]>((previous) => {
    const published = venue.mirror()?.effectCatalog ?? venue.deviceDoc()?.effectCatalog;
    const commands = venue.deviceDoc()?.commandCatalog;
    const library = roomEffectLibrary(published, commands);
    const queried = queriedEffectCatalog();
    // A direct room-shaped read is newer than a pushed roomless card, but only for that missing fact.
    // Once the pushed catalog carries the same room declaration it wins again, so installs, removals and
    // availability changes keep following the controller's live document rather than a route-local copy.
    const next = library.length ? library : queried;
    // Hold the PREVIOUS objects when the library still says the same thing. The mirror re-delivers the
    // whole controller node several times a second, and re-parsing it yields equal-but-new objects; on
    // reference-reconciled `<For>`s that rebuilt every card and every control row twice a second, which
    // is what pulled the range input out from under a dragging pointer. See `sameEffectLibrary`.
    return previous && sameEffectLibrary(previous, next) ? previous : next;
  }, []);
  const cardStartStates = createTreeCardStartStates(venue, effectCatalog, layout);
  const visibleEffects = createMemo(() => filterEffectPackages(effectCatalog(), effectQuery(), effectFilter()));
  /**
   * The cards are keyed on packageId (H39). A start moves one card installed → starting → running, and
   * the controller republishes the whole catalog for it; a list keyed on the item objects rebuilt EVERY
   * card node for that one field, which is what reset the list's scroll. Keyed on the id, a state change
   * updates the one card in place and no card node is ever replaced.
   */
  const visibleEffectIds = createMemo(() => visibleEffects().map((item) => item.packageId), [], { equals: sameIds });
  const visibleEffectById = createMemo(() => new Map(visibleEffects().map((item) => [item.packageId, item])));
  /** Play on the selected card (C26) is the same start as double-click. */
  const canRunEffects = createMemo(() => can(venue.role(), 'effects.run'));

  /** The package the room is running, whatever route the console is on. */
  const runningPackage = createMemo(() => effectPackageFromMirror(venue.mirror()?.effect)
    ?? (venue.controllerState()?.effects ?? []).map(effectPackageFromLiveEffect).find(Boolean));
  const runningPackageId = createMemo(() => runningCardId(venue.mirror()?.effect, venue.controllerState()?.effects ?? []));
  const runningRoomLabel = createMemo(() => {
    const id = runningPackageId();
    if (!id) return 'No room effect';
    // The controller publishes the running package's own name beside its id, so the header names what is
    // running even before the library has arrived — and never falls back to a slug of the id.
    return effectCatalog().find((item) => item.packageId === id)?.name
      || runningPackage()?.name
      || 'No room effect';
  });
  const openEffect = createMemo(() => (
    visibleEffects().find((item) => item.packageId === selectedPackageId())
  ));

  /**
   * The card whose start or stop is still in the air.
   *
   * A double-clicked card is selected in the same frame as the click — that is the operator's half of
   * the exchange, and it is done immediately — and this is the system's half: the card keeps a spinner
   * from the double-click until the controller has answered. The request names the package until
   * whichever panel owns it takes it up, and the action key names it from there, so the spinner
   * survives that handover instead of blinking between the two.
   */
  const busyPackageId = createMemo(() => {
    const requested = pendingStart();
    if (requested) return requested;
    const action = venue.actionBusy();
    if (action === 'start-effect-package' || TREE_CARD_START_ACTION_KEYS.includes(action)) return selectedPackageId();
    if (action === 'stop-effect') return runningPackageId() || selectedPackageId();
    return '';
  });

  let adoptedRoomEffect = '';
  let operatorSelectedRoomEffect = false;
  createEffect(() => {
    const id = runningPackageId();
    if (!id) {
      adoptedRoomEffect = '';
      return;
    }
    if (id !== adoptedRoomEffect) {
      adoptedRoomEffect = id;
      // Adopt the controller's running effect when the screen first hydrates, but never let a
      // delayed mirror edge undo an operator's explicit card choice while they are configuring it.
      if (!operatorSelectedRoomEffect) setSelectedPackageId(id);
    }
  });

  createEffect(() => {
    const revision = venue.rigReloadRevision();
    if (revision > observedRigReloadRevision) setRigReloadNotice(ROOM_UPDATED_NOTICE);
    observedRigReloadRevision = revision;
  });

  /**
   * One bounded catalog read per console route, made only when the controller has published no catalog:
   * with the room's layout when there is one, roomless otherwise.
   */
  async function readEffectCatalog(roomLayout?: EffectLayout): Promise<void> {
    try {
      const result = await venue.controllerCall<EffectPackageListResult>(
        'list_effect_packages',
        roomLayout ? { layout: roomLayout } : {},
      );
      setQueriedCatalog(result.effects ?? []);
    } catch (error) {
      reportError('effect library', error, "The controller didn't answer about its effects.");
    }
  }

  createEffect(() => {
    const pushed = venue.mirror()?.effectCatalog ?? venue.deviceDoc()?.effectCatalog;
    const commands = venue.deviceDoc()?.commandCatalog;
    const published = roomEffectLibrary(pushed, commands);
    if (published.length) return;
    const roomLayout = layout()?.layout;
    const key = `${venue.selectedDeviceId()}:${venue.controlTransport()}:${roomLayout?.layoutDigest ?? 'roomless'}`;
    if (!venue.controlsEnabled() || catalogReadKey === key) return;
    catalogReadKey = key;
    void readEffectCatalog(roomLayout);
  });

  /** The one word a card carries on its right: what it is DOING, and nothing else. */
  function cardBadge(item: EffectPackageItem): string {
    if (runningPackageId() === item.packageId) return 'Running';
    const readout = runningPackage();
    if (readout?.packageId === item.packageId && readout.state === 'stopped') return readout.stateLabel;
    return '';
  }

  return (
    <>
      <ScreenHeader
        title="Effects"
        description="Choose a room-wide effect, shape it live, and see exactly what the controller is running."
      />
      <ControlGate>
        <Show when={rigReloadNotice()}>
          <InlineNotice tone="success"><span data-testid={TEST_IDS.rigMapReloaded}>{rigReloadNotice()}</span></InlineNotice>
        </Show>

        <Show when={outputView.active()}>
          <section class="surface effect-preview-stage" data-testid={TEST_IDS.effectsRoomLive}>
            {/* The time's row is held from the start, so its arrival at helloAck never moves the drawing. */}
            <div class="output-view-indicator-slot">
              <Show when={outputView.remaining()}>{(remaining) => (
                <OutputViewIndicator remaining={remaining()} stage testId={TEST_IDS.outputViewStageIndicator} />
              )}</Show>
            </div>
            <div class="output-view-frame">
              <RoomCanvas
                model={venue.roomModel()}
                levels={outputView.levels()}
                unknownKeys={outputView.unknownKeys()}
                fixtures={liveRoomFixtures() as ReadonlyMap<string, FixtureValues>}
                class="effect-room-canvas"
                testId={TEST_IDS.effectRoomCanvas}
                fitLayoutAspect
              />
              <span class="output-view-wire" role="status" data-testid={TEST_IDS.outputViewWire}
                data-state={outputView.wire().state}>{outputViewWireLabel(outputView.wire())}</span>
            </div>
            <div class="effect-detail-tools output-view-stage-tools">
              <Show when={outputView.reason()}><span role="status" data-testid={TEST_IDS.outputViewReason}>{outputView.reason()}</span></Show>
              <Show when={outputView.sessionSwitchable()}>
                <span class="output-view-duration">Stream indefinitely
                  <Switch checked={outputView.sessionExtended()} label="Stream indefinitely" testId={TEST_IDS.outputViewSessionExtended}
                    onChange={(value) => void outputView.setSessionExtended(value)} />
                </span>
              </Show>
              <button
                type="button"
                class="button output-view-button active"
                onClick={outputView.stop}
                data-testid={TEST_IDS.outputViewStop}
              >Stop live view</button>
            </div>
          </section>
        </Show>

        <section class="surface effects-running" classList={{ live: Boolean(runningPackageId()) }}>
          <span aria-hidden="true"><i /></span>
          <div>
            <small>Room-wide effect</small>
            <strong data-testid={TEST_IDS.runningEffect}>Running: {runningRoomLabel()}</strong>
          </div>
        </section>

        <div class="effect-screen-layout">
        <CardSection
          class="effect-library"
          bodyClass={canRunEffects() ? 'effect-card-list can-play' : 'effect-card-list'}
          label="Room-wide effects"
          bodyTestId={TEST_IDS.effectCardGrid}
          tools={(
            <div class="effect-library-tools">
              <div class="effect-filter" role="group" aria-label="Filter effects">
                <button
                  type="button"
                  classList={{ active: effectFilter() === 'all' }}
                  aria-pressed={effectFilter() === 'all'}
                  onClick={() => setEffectFilter('all')}
                  data-testid={TEST_IDS.effectsFilterAll}
                >All</button>
                <button
                  type="button"
                  classList={{ active: effectFilter() === 'music-reactive' }}
                  aria-pressed={effectFilter() === 'music-reactive'}
                  onClick={() => setEffectFilter('music-reactive')}
                  data-testid={TEST_IDS.effectsFilterMusic}
                >Music-reactive</button>
              </div>
              <label class="library-search effect-search">
                <Search size={17} aria-hidden="true" />
                <input
                  type="search"
                  value={effectQuery()}
                  onInput={(event) => setEffectQuery(event.currentTarget.value)}
                  placeholder="Search effects"
                  aria-label="Search effects"
                />
              </label>
            </div>
          )}
          footer={(
            <Show when={!visibleEffects().length}>
              <p class="quiet-empty card-section-note">
                {effectQuery().trim()
                  ? `No effects match “${effectQuery().trim()}”.`
                  : effectFilter() === 'music-reactive'
                    ? 'No music-reactive effects are available.'
                    : 'No effects are available.'}
              </p>
            </Show>
          )}
        >
          <For each={visibleEffectIds()}>
            {(packageId) => {
              // The row outlives catalog republishes; it reads its item through the id (H39).
              const item = createMemo<EffectPackageItem>(
                (previous) => visibleEffectById().get(packageId) ?? previous,
                visibleEffectById().get(packageId)!,
              );
              const running = () => runningPackageId() === packageId;
              const selected = () => selectedPackageId() === packageId;
              const badge = () => cardBadge(item());
              const busy = () => busyPackageId() === packageId;
              const playable = () => selected() && item().available && !running() && canRunEffects();
              const startState = cardStartStates.get(packageId);
              const cardRefusalReason = () => playable() ? startState?.readiness().reason ?? '' : '';
              const cardSentence = () => item().available
                ? cardRefusalReason() || item().description
                : operatorUnavailableReason(item()) || item().description;
              return (
                <div class="effect-card-slot">
                <button
                  type="button"
                  class="effect-card busy-host"
                  classList={{ selected: selected(), running: running(), unavailable: !item().available, 'is-busy': busy() }}
                  aria-pressed={selected()}
                  onClick={() => {
                    operatorSelectedRoomEffect = true;
                    setSelectedPackageId(packageId);
                  }}
                  /*
                   * Double-click starts it. It is the Play control's own action — same command, same
                   * remembered settings, same takeover prompt — reachable without scrolling past the
                   * whole panel to find the button. Single click still only selects, so nothing starts
                   * by accident, and the grid's own scrolling is untouched: a dblclick is delivered
                   * after the two clicks, never in place of a drag or a scroll.
                   */
                  onDblClick={() => {
                    operatorSelectedRoomEffect = true;
                    setSelectedPackageId(packageId);
                    requestStart(packageId);
                  }}
                  data-package-id={packageId}
                  data-origin={item().origin}
                  data-music-reactive={effectPackageUsesMusic(item()) ? 'true' : 'false'}
                  data-running={running() ? 'true' : 'false'}
                  data-busy={busy() ? 'true' : 'false'}
                  data-testid={testIdFor.effectCard(packageId)}
                >
                  {/* A card is a button, and starts or stops like one: the spinner takes its place while
                      it works, and the card neither moves nor resizes. */}
                  <span class="busy-content">
                    <span class="effect-card-copy">
                      <strong>{item().name}</strong>
                      {/*
                        * A card with nothing to say says nothing, rather than reserving an empty line that
                        * would push its name off centre. A controller that publishes no sentence for an
                        * effect has not given one to show, and the console does not write one for it.
                        */}
                      <Show when={cardSentence()}>
                        {(sentence) => <small classList={{ 'effect-card-reason': Boolean(cardRefusalReason()) }}>{sentence()}</small>}
                      </Show>
                    </span>
                    <Show when={badge()}>
                      {(text) => (
                        <span class="effect-card-status" classList={{ live: running() }}>{text()}</span>
                      )}
                    </Show>
                  </span>
                  <Show when={busy()}><BusySpinner /></Show>
                </button>
                {/* A button cannot hold a button, so the selected card's Play sits beside it in the same
                    slot, absolutely placed inside the card's fixed row: showing it moves nothing (B82). It
                    runs the very start double-click runs. */}
                <Show when={playable()}>
                  <button
                    type="button"
                    class="button effect-card-play"
                    disabled={busy() || startState?.readiness().enabled === false}
                    onClick={() => {
                      operatorSelectedRoomEffect = true;
                      requestStart(packageId);
                    }}
                    data-testid={testIdFor.effectCardPlay(packageId)}
                  >Play</button>
                </Show>
                </div>
              );
            }}
          </For>
        </CardSection>

        <div class="effect-detail inner-scroll">
          <Show when={outputView.visible() && !outputView.active()}>
            <div class="effect-detail-tools output-view-start">
              <button
                type="button"
                class="button output-view-button"
                disabled={!outputView.available()}
                onClick={() => void outputView.start()}
                data-testid={TEST_IDS.outputViewStart}
              >Live view</button>
              <span class="output-view-duration">Stream indefinitely
                <Switch checked={outputView.extended()} label="Stream indefinitely" testId={TEST_IDS.outputViewExtended}
                  onChange={outputView.setExtended} />
              </span>
            </div>
            <Show when={outputView.holder()}>{(holder) => <div class="output-view-holder" data-testid={TEST_IDS.outputViewHolder}>
              {holder().displayName} is viewing{holderDetail(holder())}
              <Show when={can(venue.role(), 'stream.disconnect')}>
                <button type="button" class="button" onClick={() => setOutputTakeoverOpen(true)} data-testid={TEST_IDS.outputViewTakeover}>Take over</button>
              </Show>
            </div>}</Show>
            <Show when={outputView.reason()}><span role="status" data-testid={TEST_IDS.outputViewReason}>{outputView.reason()}</span></Show>
          </Show>
          <ConfirmModal open={outputTakeoverOpen()} title="Take over?" detail={outputView.holder() ? `${outputView.holder()!.displayName} is viewing` : ''}
            confirmLabel="Take over" onConfirm={() => { setOutputTakeoverOpen(false); void outputView.takeOver(); }}
            onCancel={() => setOutputTakeoverOpen(false)} />
        {/*
          * ONE choice, on the card's settings shape (S34): a tree-shaped card draws its own panel through
          * the adapter registry, and every other card is the one generic package panel. There is no
          * branch here on which card is open.
          */}
        <Show when={!venue.rigUnassigned()}>
          <Show when={openEffect()}>
            {(item) => (
              <Show
                when={treeCardPanel(item())}
                keyed
                fallback={(
                  <EffectPackagePanel
                    item={item()}
                    layout={layout()}
                    roomPath={venue.roomLayout()}
                    loading={layoutLoading()}
                    unavailableDetail={venue.effectLayoutDetail()}
                    startRequested={() => pendingStart() === item().packageId}
                    onStartHandled={() => setPendingStart('')}
                  />
                )}
              >
                {(panel) => (
                  <Dynamic
                    component={panel}
                    item={item()}
                    catalog={effectCatalog}
                    startState={cardStartStates.get(item().packageId)}
                    layout={layout()}
                    loading={layoutLoading()}
                    unavailableDetail={venue.effectLayoutDetail()}
                    startRequested={() => pendingStart() === item().packageId}
                    onStartHandled={() => setPendingStart('')}
                  />
                )}
              </Show>
            )}
          </Show>
        </Show>
          </div>
        </div>
      </ControlGate>
    </>
  );
}
